/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { truncateMiddle } from '../../utils/StringUtils';
import { StyledTooltip, StyledTooltipContent } from './Tooltip';

export interface TruncateProps {
  text: string;
  splitInMiddle?: boolean;
  disableTooltip?: boolean;
  tooltipMaxWidthPx?: number;
  maxCharacters?: number;
  xOffsetPx?: number;
  yOffsetPx?: number;
}

export interface TruncateState {
  isTruncated: boolean;
  hovering: boolean;
}

const StyledLabel = styled.div`
  white-space: nowrap;
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;
const TruncateWithTooltip = (props: TruncateProps) => {
  const { text, splitInMiddle, disableTooltip, tooltipMaxWidthPx, maxCharacters, xOffsetPx, yOffsetPx } = props;
  const element = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);
  const [hovering, setHovering] = useState<boolean>(false);

  useEffect(() => {
    if (shouldShowTooltip()) {
      setIsTruncated(true);
    }
  }, []);
  const handleMouseEnter = () => {
    const { maxCharacters, text } = props;

    if (isTruncated || (text && maxCharacters && text.length >= maxCharacters)) {
      setHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (isTruncated || (text && maxCharacters && text.length >= maxCharacters)) {
      setHovering(false);
    }
  };

  const shouldShowTooltip = (): boolean => {
    let shouldShowTooltip = false;
    if (element.current) {
      const { offsetWidth, scrollWidth } = element.current;
      shouldShowTooltip = offsetWidth < scrollWidth;
    }
    return shouldShowTooltip;
  };

  return (
    <React.Fragment>
      {splitInMiddle ? (
        <React.Fragment>
          <div {...props} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} data-full-text={text}>
            {truncateMiddle(text, maxCharacters)}
          </div>

          <React.Fragment>
            {!disableTooltip && maxCharacters && text && text.length >= maxCharacters && hovering && (
              <StyledTooltip
                className="tooltip__body"
                maxWidth={tooltipMaxWidthPx}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <StyledTooltipContent>{text}</StyledTooltipContent>
              </StyledTooltip>
            )}
          </React.Fragment>
        </React.Fragment>
      ) : (
        <StyledLabel ref={element} {...props} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {text}
        </StyledLabel>
      )}
      {!disableTooltip && isTruncated && hovering && (
        <StyledTooltip
          className="tooltip__body"
          maxWidth={tooltipMaxWidthPx}
          xOffset={xOffsetPx}
          yOffsetPx={yOffsetPx}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <StyledTooltipContent>{text}</StyledTooltipContent>
        </StyledTooltip>
      )}
    </React.Fragment>
  );
};
export default TruncateWithTooltip;
